import { Link } from 'react-router';

import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { usePartsEnvironment } from '@/features/storefront/parts/context';
import { partIsWide, partOwnsSpacing, type PartPresentation } from '@/features/storefront/parts/style';
import { useStorefrontPaths } from '@/features/storefront/paths';
import { partChoice, partFlag, partNumber, type PagePart } from '@/features/storefront/sections/parts';
import { safeHref } from '@/features/storefront/sections/primitives';
import { STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';

/**
 * The furniture a merchant may stand anywhere.
 *
 * Six components that read nothing from the page they are on: a heading, a
 * paragraph, a button, a picture, a rule and some room. They are what makes the
 * component tree more than a switchboard for what the theme already drew - a
 * size-guide link beside the buy button, a "made in Jaipur" line under the
 * price, a returns badge in the totals panel are all this, placed.
 *
 * Everything merchant-authored that could carry a link goes through
 * {@link safeHref} on the way to an `<a>`, exactly as the sections do. The API
 * refuses anything else on save; this is the second lock.
 */

export interface CommonPartProps {
  part: PagePart;
  presentation?: PartPresentation;
}

export function CustomHeadingPart({ part }: CommonPartProps) {
  const words = part.label.trim();
  if (!words) return null;

  const level = partChoice(part, 'level');
  const style = level === 'p' ? undefined : STOREFRONT_HEADING_STYLE;

  // Sized and coloured by the wrapper - see `parts/style.ts` - so the heading
  // level here is about what it *means*, not about how loud it is.
  if (level === 'h3') return <h3 style={style}>{words}</h3>;
  if (level === 'p') return <p>{words}</p>;
  return <h2 style={style}>{words}</h2>;
}

export function CustomTextPart({ part }: CommonPartProps) {
  const words = part.body.trim();
  if (!words) return null;
  // Merchant copy, rendered as text: a paragraph part that accepted markup
  // would be a second sanitiser to keep correct, and the description part is
  // already where rich text belongs.
  return <p className="whitespace-pre-line leading-relaxed">{words}</p>;
}

/**
 * A link that looks like a button.
 *
 * Inert in the customiser, like every other link in a preview: a merchant
 * clicking their own size guide while designing must not be thrown out of the
 * editor.
 */
export function CustomButtonPart({ part, presentation }: CommonPartProps) {
  const paths = useStorefrontPaths();
  const preview = usePartsEnvironment()?.includeHidden ?? false;
  const words = part.label.trim();
  const href = safeHref(partChoice(part, 'href'));
  if (!words) return null;

  const style = partChoice(part, 'style');
  const wide = partIsWide(part, presentation);
  const className = cn(
    'inline-flex h-12 items-center justify-center text-sm font-semibold',
    style !== 'link' && 'border px-7',
    wide ? 'w-full' : 'w-auto',
  );
  const styles =
    style === 'link'
      ? { color: 'var(--storefront-primary)', textDecoration: 'underline' }
      : {
          backgroundColor: style === 'solid' ? 'var(--storefront-primary)' : 'transparent',
          color: style === 'solid' ? 'var(--storefront-background)' : 'var(--storefront-primary)',
          borderColor: 'var(--storefront-primary)',
          borderRadius: 'var(--storefront-radius)',
        };

  if (!href || preview) {
    return (
      <span className={className} style={styles}>
        {words}
      </span>
    );
  }

  const external = href.startsWith('http') || partFlag(part, 'new_tab');
  if (external) {
    return (
      <a
        href={href}
        target={partFlag(part, 'new_tab') ? '_blank' : undefined}
        rel={partFlag(part, 'new_tab') ? 'noreferrer noopener' : undefined}
        className={className}
        style={styles}
      >
        {words}
      </a>
    );
  }

  return (
    <Link to={paths.menu(href)} className={className} style={styles}>
      {words}
    </Link>
  );
}

export function CustomImagePart({ part }: CommonPartProps) {
  const paths = useStorefrontPaths();
  const preview = usePartsEnvironment()?.includeHidden ?? false;
  const url = partChoice(part, 'image_url').trim();
  if (!url) return null;

  const width = partNumber(part, 'max_width');
  const picture = (
    <StorefrontImage
      url={url}
      sizes={width > 0 ? `${width}px` : '100vw'}
      alt={partChoice(part, 'alt')}
      className="h-auto w-full"
      style={{
        maxWidth: width > 0 ? `${width}px` : undefined,
        borderRadius: 'var(--storefront-radius)',
      }}
    />
  );

  const href = safeHref(partChoice(part, 'href'));
  if (!href || preview) return picture;
  return href.startsWith('http') ? (
    <a href={href}>{picture}</a>
  ) : (
    <Link to={paths.menu(href)}>{picture}</Link>
  );
}

export function DividerPart({ part }: CommonPartProps) {
  return (
    <hr
      className={cn(partOwnsSpacing(part) && 'my-6', 'border-0 border-t')}
      style={{ borderColor: 'var(--storefront-surface)' }}
    />
  );
}

export function SpacerPart({ part }: CommonPartProps) {
  return <div aria-hidden style={{ height: `${partNumber(part, 'height') || 32}px` }} />;
}
